import { Component, OnInit, OnDestroy } from '@angular/core';
import { Subject } from 'rxjs';
import { takeUntil, filter } from 'rxjs/operators';
import { GlobalSetupService } from '../../services/global-setup.service';
import { RolePermissionsService } from '../../services/role-permissions.service';
import { ButtonModule } from 'primeng/button';
import { ProgressSpinner } from 'primeng/progressspinner';
import { DialogModule } from 'primeng/dialog';
import { Router, NavigationEnd } from '@angular/router';
import { CommonModule } from '@angular/common';

@Component({
  selector: 'app-global-setup-modal',
  imports: [CommonModule, ButtonModule, ProgressSpinner, DialogModule],
  standalone: true,
  templateUrl: './global-setup-modal.component.html',
  styleUrl: './global-setup-modal.component.scss'
})
export class GlobalSetupModalComponent implements OnInit, OnDestroy {
  showModal = false;
  isChecking = false;
  private destroy$ = new Subject<void>();

  constructor(
    private globalSetupService: GlobalSetupService,
    private router: Router,
    private rolePermissionsService: RolePermissionsService
  ) { }

  ngOnInit(): void {
    // Subscribe to setup required state
    this.globalSetupService.setupRequired$
      .pipe(takeUntil(this.destroy$))
      .subscribe(required => {
        this.updateModalVisibility(required);
      });

    // Subscribe to checking state
    this.globalSetupService.isChecking$
      .pipe(takeUntil(this.destroy$))
      .subscribe(checking => {
        this.isChecking = checking;
      });

    // Listen to route changes - only update visibility based on current state
    this.router.events
      .pipe(
        filter(event => event instanceof NavigationEnd),
        takeUntil(this.destroy$)
      )
      .subscribe(() => {
        // Only update modal visibility based on current setup state, don't trigger new checks
        const currentSetupRequired = this.globalSetupService.getCurrentSetupRequiredState();
        this.updateModalVisibility(currentSetupRequired);
      });
  }

  private updateModalVisibility(setupRequired: boolean): void {
    const currentPath = window.location.pathname;
    const canWrite = this.rolePermissionsService.canCurrentUserWrite();
    const shouldShow = setupRequired && canWrite && this.shouldShowModalForCurrentPage(currentPath);
    
    // Only update if the state actually changes to prevent unnecessary DOM updates
    if (this.showModal !== shouldShow) {
      this.showModal = shouldShow;
    }
  }

  private shouldShowModalForCurrentPage(currentPath: string): boolean {
    // Don't show on login or settings pages
    if (currentPath === '/login' || currentPath === '/settings') {
      return false;
    }
    
    // Only show on LinkedIn and outreach pages
    return currentPath.includes('/linkedin') || currentPath.includes('/email-outreach');
  }


  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  navigateToSettings(): void {
    this.showModal = false;
    this.globalSetupService.navigateToSettings();
  }
}